<template>
  <v-container fluid class="d-flex justify-center" :style="`margin-left: ${margin}`">
    <div style="width: 100%; max-width: 1200px;">

      <!-- Header -->
      <div class="table-header-filters pa-4 mb-6">
        <v-row dense align="center">
          <v-col cols="12" sm="auto">
            <div class="period-pills">
              <span
                v-for="p in periods"
                :key="p.value"
                class="period-pill"
                :class="{ active: period === p.value }"
                @click="onPeriodClick(p.value)"
              >{{ p.label }}</span>
            </div>
          </v-col>

          <!-- Custom date inputs -->
          <v-col v-if="period === 'custom'" cols="12" sm="auto" class="d-flex align-center" style="gap: 6px;">
            <v-menu v-model="menuFrom" offset-y :close-on-content-click="false" min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <button class="period-pill date-pill" v-bind="attrs" v-on="on">
                  <v-icon x-small class="mr-1">mdi-calendar-start</v-icon>
                  {{ formatDate(customFrom) }}
                </button>
              </template>
              <v-date-picker v-model="customFrom" no-title scrollable @input="menuFrom = false; fetchData()" />
            </v-menu>
            <span class="date-pill-sep">—</span>
            <v-menu v-model="menuTo" offset-y :close-on-content-click="false" min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <button class="period-pill date-pill" v-bind="attrs" v-on="on">
                  <v-icon x-small class="mr-1">mdi-calendar-end</v-icon>
                  {{ formatDate(customTo) }}
                </button>
              </template>
              <v-date-picker v-model="customTo" no-title scrollable @input="menuTo = false; fetchData()" />
            </v-menu>
          </v-col>

          <v-col cols="auto" class="ml-auto">
            <v-btn icon :to="{ name: `${targetModifier}.all-reports.reportMenu` }" class="close-btn">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </div>


      <!-- Loading -->
      <div v-if="loading" class="text-center py-12">
        <v-progress-circular indeterminate color="cyan darken-1" size="48" />
        <div class="mt-4 grey--text">Učitavanje podataka...</div>
      </div>

      <template v-else>

        <!-- KPI kartice -->
        <v-row class="mb-6">
          <v-col cols="12" sm="4">
            <v-card outlined rounded="lg" class="pa-4 text-center">
              <div class="kpi-label">Ukupno napojnice</div>
              <div class="kpi-value" style="color: #00BCD4">{{ formatEur(totalEur) }}</div>
              <div class="kpi-sub">{{ totalCount }} napojnica</div>
            </v-card>
          </v-col>
          <v-col cols="6" sm="4">
            <v-card outlined rounded="lg" class="pa-4 text-center">
              <div class="kpi-label">Gotovina</div>
              <div class="kpi-value" style="color: #4CAF50">{{ formatEur(gotovinaEur) }}</div>
              <div class="kpi-sub">{{ gotovinaPercent.toFixed(1) }}%</div>
            </v-card>
          </v-col>
          <v-col cols="6" sm="4">
            <v-card outlined rounded="lg" class="pa-4 text-center">
              <div class="kpi-label">Kartica</div>
              <div class="kpi-value" style="color: #2196F3">{{ formatEur(karticaEur) }}</div>
              <div class="kpi-sub">{{ karticaPercent.toFixed(1) }}%</div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Split bar -->
        <v-card outlined rounded="lg" class="pa-6 mb-6">
          <div class="section-title mb-4">Podjela po načinu plaćanja</div>
          <div class="d-flex" style="height: 32px; border-radius: 8px; overflow: hidden;">
            <div :style="{ width: gotovinaPercent + '%', background: '#4CAF50', transition: 'width 0.6s ease' }"></div>
            <div :style="{ width: karticaPercent + '%', background: '#2196F3', transition: 'width 0.6s ease' }"></div>
          </div>
          <div class="d-flex mt-2" style="gap: 16px;">
            <div class="d-flex align-center" style="gap: 6px;">
              <div style="width: 10px; height: 10px; border-radius: 50%; background: #4CAF50;"></div>
              <span style="font-size: 12px;">Gotovina {{ gotovinaPercent.toFixed(1) }}%</span>
            </div>
            <div class="d-flex align-center" style="gap: 6px;">
              <div style="width: 10px; height: 10px; border-radius: 50%; background: #2196F3;"></div>
              <span style="font-size: 12px;">Kartica {{ karticaPercent.toFixed(1) }}%</span>
            </div>
          </div>
        </v-card>

        <!-- Horizontalni barovi -->
        <v-card outlined rounded="lg" class="pa-6">
          <div class="section-title mb-4">Iznos po načinu plaćanja</div>
          <!-- Gotovina bar -->
          <div class="mb-4">
            <div class="d-flex justify-space-between mb-1">
              <span style="font-size: 13px; font-weight: 500">💵 Gotovina</span>
              <span style="font-size: 13px; font-weight: 600; color: #4CAF50">{{ formatEur(gotovinaEur) }}</span>
            </div>
            <div class="bar-track">
              <div class="bar-fill" :style="{ width: (totalEur > 0 ? gotovinaEur / totalEur * 100 : 0) + '%', background: '#4CAF50' }"></div>
            </div>
          </div>
          <!-- Kartica bar -->
          <div>
            <div class="d-flex justify-space-between mb-1">
              <span style="font-size: 13px; font-weight: 500">💳 Kartica</span>
              <span style="font-size: 13px; font-weight: 600; color: #2196F3">{{ formatEur(karticaEur) }}</span>
            </div>
            <div class="bar-track">
              <div class="bar-fill" :style="{ width: (totalEur > 0 ? karticaEur / totalEur * 100 : 0) + '%', background: '#2196F3' }"></div>
            </div>
          </div>
        </v-card>

      </template>
    </div>
  </v-container>
</template>

<script>
import { getFirestore, collection, query, where, getAggregateFromServer, sum, count } from 'firebase/firestore'
import state from '@/state'
import EventBus from '@/plugins/event-bus'

const today = new Date().toISOString().substr(0, 10)

export default {
  name: 'NapojniceReport',
  inject: ['showMsgBox', 'showLoader', 'hideLoader'],

  data () {
    return {
      margin: '0px',
      targetModifier: '',
      loading: false,
      period: 'month',
      menuFrom: false,
      menuTo: false,
      customFrom: today,
      customTo: today,
      periods: [
        { value: 'today', label: 'Danas' },
        { value: 'week',  label: 'Tjedan' },
        { value: 'month', label: 'Ovaj mjesec' },
        { value: 'custom', label: 'Prilagođeno' },
      ],
      data: null // { gotovinaCents, karticaCents, gotovinaCount, karticaCount }
    }
  },

  computed: {
    gotovinaEur () { return this.data ? this.data.gotovinaCents / 100 : 0 },
    karticaEur ()  { return this.data ? this.data.karticaCents / 100 : 0 },
    gotovinaCount () { return this.data ? this.data.gotovinaCount : 0 },
    karticaCount ()  { return this.data ? this.data.karticaCount : 0 },
    totalEur ()   { return this.gotovinaEur + this.karticaEur },
    totalCount () { return this.gotovinaCount + this.karticaCount },
    gotovinaPercent () { return this.totalEur > 0 ? this.gotovinaEur / this.totalEur * 100 : 0 },
    karticaPercent ()  { return this.totalEur > 0 ? this.karticaEur / this.totalEur * 100 : 0 },
  },

  async created () {
    this.margin = state.getMargin()
    const sideNav = this.$route.meta?.sideNav
    if (sideNav === 'Company') this.targetModifier = 'company'
    else if (sideNav === 'PointOfSale') this.targetModifier = 'pointOfSale'
    else if (sideNav === 'CashRegister') this.targetModifier = 'cashRegister'
    EventBus.$on('set-mini', () => { this.margin = state.getMargin() })
    await this.fetchData()
  },

  beforeDestroy () {
    EventBus.$off('set-mini')
  },

  methods: {
    formatEur (v) {
      return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(v)
    },

    formatDate (d) {
      if (!d) return ''
      const [y, m, day] = d.split('-')
      return `${day}.${m}.${y}`
    },

    onPeriodClick (value) {
      this.period = value
      if (value !== 'custom') this.fetchData()
    },

    getDateRange () {
      const now = new Date()
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())

      if (this.period === 'today') {
        return { from: today, to: today }
      }

      if (this.period === 'week') {
        const day = today.getDay() || 7
        const mon = new Date(today)
        mon.setDate(today.getDate() - day + 1)
        return { from: mon, to: today }
      }

      if (this.period === 'month') {
        const from = new Date(now.getFullYear(), now.getMonth(), 1)
        return { from, to: today }
      }

      // custom
      const from = new Date(this.customFrom)
      const to = new Date(this.customTo)
      return { from, to }
    },

    async fetchData () {
      this.loading = true
      try {
        const { from, to } = this.getDateRange()
        const fromSec = Math.floor(from.getTime() / 1000)
        // to: end of day
        const toEnd = new Date(to.getFullYear(), to.getMonth(), to.getDate(), 23, 59, 59)
        const toSec = Math.floor(toEnd.getTime() / 1000)

        const companyId = state.getCurrentCompany().id
        const db = getFirestore()
        const col = collection(db, 'tips')

        const baseQuery = query(col,
          where('company_id', '==', companyId),
          where('created', '>=', fromSec),
          where('created', '<=', toSec)
        )
        const karticaQuery = query(col,
          where('company_id', '==', companyId),
          where('created', '>=', fromSec),
          where('created', '<=', toSec),
          where('tip_payment_method', '==', 'KARTICA')
        )

        const [total, kartica] = await Promise.all([
          getAggregateFromServer(baseQuery, { total: sum('tip_amount'), cnt: count() }),
          getAggregateFromServer(karticaQuery, { total: sum('tip_amount'), cnt: count() })
        ])

        const totalCents = total.data().total || 0
        const totalCount = total.data().cnt || 0
        const kartCents = kartica.data().total || 0
        const kartCount = kartica.data().cnt || 0

        this.data = {
          gotovinaCents: totalCents - kartCents,
          gotovinaCount: totalCount - kartCount,
          karticaCents: kartCents,
          karticaCount: kartCount
        }
      } catch (err) {
        console.error('NapojniceReport:', err)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.close-btn {
  width: 36px !important;
  height: 36px !important;
  min-width: 36px !important;
  border-radius: 10px !important;
  background: rgba(255, 255, 255, 0.95) !important;
  border: 1px solid rgba(229, 57, 53, 0.2) !important;
  color: #e53935 !important;
  transition: all 0.2s ease !important;
  box-shadow: none !important;
}
.close-btn:hover {
  background: rgba(229, 57, 53, 0.07) !important;
  box-shadow: 0 0 0 3px rgba(229, 57, 53, 0.1) !important;
}
.table-header-filters {
  position: sticky;
  top: 64px;
  z-index: 10;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.period-pills {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.period-pill {
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  color: #6B7280;
  cursor: pointer;
  border: 1.5px solid #E2E8F0;
  background: #fff;
  transition: all 0.15s;
  white-space: nowrap;
  outline: none;
}
.period-pill:hover {
  border-color: #00BCD4;
  color: #00838F;
  background: rgba(0, 188, 212, 0.05);
}
.period-pill.active {
  border-color: rgba(0, 188, 212, 0.5);
  background: rgba(0, 188, 212, 0.1);
  color: #00838F;
  font-weight: 600;
}
.date-pill {
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.3px;
}
.date-pill-sep {
  font-size: 13px;
  color: #9CA3AF;
  font-weight: 500;
}
.bar-track {
  height: 10px;
  border-radius: 5px;
  background: #F1F5F9;
  overflow: hidden;
}
.bar-fill {
  height: 100%;
  border-radius: 5px;
  transition: width 0.6s ease;
}
.kpi-label {
  font-size: 12px;
  color: #6B7280;
  margin-bottom: 4px;
}
.kpi-value {
  font-size: 22px;
  font-weight: 700;
  margin-bottom: 2px;
}
.kpi-sub {
  font-size: 12px;
  color: #9CA3AF;
}
.section-title {
  font-size: 16px;
  font-weight: 700;
  color: #2d3748;
}
</style>
